import { cn } from "@/utils";
import { IntegrationConnection, Skill } from "@cohesive/api-client";
import {
  ConnectionPlugin,
  PlaceholderPlugin,
  SkillPlugin,
  TextReplacePlugin,
} from "@cohesive/ui-components";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { $getRoot } from "lexical";

interface EditorProps {
  skills: Skill[];
  integrationConnections: IntegrationConnection[];
  className?: string;
  onChange?: (text: string) => void;
  placeholder?: string;
  placeholderClassName?: string;
  addPlaceholderPluginMode?: "add" | "edit";
  autoFocus?: boolean;
}

export function EditorContent({
  skills,
  integrationConnections,
  placeholder,
  className,
  placeholderClassName,
  addPlaceholderPluginMode = "add",
  onChange,
  autoFocus = false,
}: EditorProps) {
  return (
    <div className="editor-container w-full">
      <PlainTextPlugin
        placeholder={
          <div
            className={cn(
              "editor-placeholder",
              "pointer-events-none",
              "px-1",
              "py-1",
              "text-neutral-400",
              "mb-2",
              "absolute",
              "top-0",
              "left-0",
              "line-clamp-2",
              "text-ellipsis",
              "overflow-hidden",
              placeholderClassName,
            )}
          >
            {placeholder}
          </div>
        }
        ErrorBoundary={LexicalErrorBoundary}
        contentEditable={
          <ContentEditable
            className={cn(
              "editor-input",
              "focus-visible:outline-hidden",
              "focus-visible:ring-ring",
              "max-h-[50vh]",
              "overflow-auto",
              "focus:ring-0",
              "focus:outline-hidden",
              "focus-visible:ring-0",
              "focus-visible:ring-offset-0",
              "bg-transparent",
              "px-1",
              "py-1",
              "mb-2",
              className,
            )}
          />
        }
      />
      <SkillPlugin enabled={true} skills={skills} />
      <ConnectionPlugin enabled={true} connections={integrationConnections} />
      <PlaceholderPlugin enabled={true} mode={addPlaceholderPluginMode} />
      {autoFocus && <AutoFocusPlugin />}
      <TextReplacePlugin />
      <HistoryPlugin />
      <ClearEditorPlugin />
      <OnChangePlugin
        onChange={(editorState) => {
          const editorStateTextString = editorState.read(() =>
            $getRoot().getTextContent(),
          );

          return onChange?.(editorStateTextString);
        }}
      />
    </div>
  );
}
